import { Alert, Col, Form, Row, Input, Checkbox, Button, Icon } from 'antd';
import './CreditCardForm.less';
import React, { useRef, useState } from 'react';
import {
    CardCvvElement,
    CardMonthElement,
    CardNumberElement,
    CardNumberElementChangeEvent,
    CardYearElement,
    IndividualElementChangeEvent,
    useRecurly,
} from '@recurly/react-recurly';
import { useSelector } from 'react-redux';
import { SSC_TOS_URL } from '../../settings/config';
import { getUserEmail } from '../../store/iam/selectors';
import { useFetchViewerCountryQuery, useLogTokenizationErrorMutation } from '../service';
import { AttemptType, Token } from '../types';

const availableCards = [
    {
        imageSrc: '/images/visa_light.png',
        name: 'Visa',
    },
    {
        imageSrc: '/images/mastercard_light.png',
        name: 'MasterCard',
    },
    {
        imageSrc: '/images/american_express_light.png',
        name: 'American Express',
    },
    {
        imageSrc: '/images/jcb_light.png',
        name: 'JCB',
    },
    {
        imageSrc: '/images/discover_light.png',
        name: 'Discover',
    },
    {
        imageSrc: '/images/diners_club_light.png',
        name: 'Diners Club',
    },
];

// One message for all errors until the logs show what Recurly returns.
const CARD_NOT_VERIFIED_MESSAGE =
    "We couldn't verify this card. Please check the details or use a different card. If it keeps happening, contact support.";

// Error fields are not enumerable, so copy them by name. Skip `stack`.
function readRecurlyError(err: Error): Record<string, unknown> {
    const recurlyError: Record<string, unknown> = {};
    Object.getOwnPropertyNames(err).forEach((name) => {
        if (name !== 'stack') {
            recurlyError[name] = (err as unknown as Record<string, unknown>)[name];
        }
    });
    return recurlyError;
}

// The API accepts only 6 digits.
function readCardBin(cardNumberValidation?: CardNumberElementChangeEvent): string | null {
    const firstSix = cardNumberValidation?.firstSix;
    return firstSix && /^[0-9]{6}$/.test(firstSix) ? firstSix : null;
}

type Props = {
    attemptType: AttemptType;
    buttonLabel: string;
    errorMessage?: string;
    isLoading?: boolean;
    showAvailableCards?: boolean;
    onToken: (token: Token) => void;
    onError: (message: string) => void;
};

export default function CreditCardForm({ attemptType, buttonLabel, errorMessage, isLoading, showAvailableCards, onToken, onError }: Props) {
    const formRef = useRef(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
    const [cardNumberValidation, setCardNumberValidation] = useState<CardNumberElementChangeEvent | undefined>();
    const [cardMonthValidation, setCardMonthValidation] = useState<IndividualElementChangeEvent | undefined>();
    const [cardYearValidation, setCardYearValidation] = useState<IndividualElementChangeEvent | undefined>();
    const [cvcValidation, setCvcValidation] = useState<IndividualElementChangeEvent | undefined>();

    const [acceptTos, setAcceptTos] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    const recurly = useRecurly();
    const { data: viewerCountry, isLoading: isLoadingViewerCountry } = useFetchViewerCountryQuery();
    const [logTokenizationError] = useLogTokenizationErrorMutation();
    const userEmail = useSelector(getUserEmail);

    const handleFocus = (event?: React.FocusEvent<HTMLInputElement, Element>) => {
        if (event?.type === 'month' || event?.type === 'year') {
            setValidationErrors((st) => ({ ...st, expirationDate: false }));
        }
        if (event?.type === 'number') {
            setValidationErrors((st) => ({ ...st, number: false }));
        }
        if (event?.type === 'cvv') {
            setValidationErrors((st) => ({ ...st, cvc: false }));
        }
    };

    const handleNameFocus = (event: React.FocusEvent<HTMLInputElement, Element>) => {
        const name = event.target.name;
        if (name === 'first_name') {
            setValidationErrors((st) => ({ ...st, firstName: false }));
        }

        if (name === 'last_name') {
            setValidationErrors((st) => ({ ...st, lastName: false }));
        }
    };

    const handleAcceptTosChange = () => {
        setAcceptTos(!acceptTos);
        setValidationErrors((st) => ({ ...st, acceptTos: false }));
    };

    // Logging must never break the payment form.
    const reportTokenizationError = (err: Error) => {
        try {
            logTokenizationError({
                attempt_type: attemptType,
                user_email: userEmail ?? '',
                first_name: firstName,
                last_name: lastName,
                card_brand: cardNumberValidation?.brand ?? null,
                card_bin: readCardBin(cardNumberValidation),
                country: viewerCountry?.country ?? null,
                recurly_error: readRecurlyError(err),
            }).unwrap().catch(() => {});
        } catch {}
    };

    const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (!formRef.current) {
            onError('Something went wrong. Please try again');
            return;
        }

        let hasErrors = false;

        if (!cardNumberValidation?.valid) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, number: true }));
        }
        if (!cardMonthValidation?.valid || !cardYearValidation?.valid) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, expirationDate: true }));
        }
        if (!cvcValidation?.valid) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, cvc: true }));
        }
        if (firstName.length === 0) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, firstName: true }));
        }
        if (lastName.length === 0) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, lastName: true }));
        }
        if (!acceptTos) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, acceptTos: true }));
        }

        if (hasErrors) {
            return;
        }

        // The country reaches Recurly inside the token, so tokenizing before it arrives produces a
        // card that looks fine and silently gets no recurring-payment mandate - the first renewal
        // months later is what reveals it. Waiting only while the request is still in flight: if it
        // failed outright, the card is taken anyway rather than losing the sale over it.
        if (isLoadingViewerCountry) {
            return;
        }

        recurly.token(formRef.current, (err, token) => {
            if (err) {
                onError(CARD_NOT_VERIFIED_MESSAGE);
                reportTokenizationError(err);
            } else {
                onToken(token);
            }
        });
    };

    return (
        <>
            {errorMessage && (
                <div className="card-validation">
                    <Alert
                        type="error"
                        showIcon
                        message={errorMessage}
                    />
                </div>
            )}

            <form
                className="credit-card-form"
                onSubmit={handleSubmit}
                ref={formRef}
            >
                {/* Recurly needs the billing country inside the token as well as on the purchase our
                    API makes; a card carrying only one of the two gets no recurring-payment mandate,
                    and its first renewal is refused. The buyer is not asked for it - the API works it
                    out from where the request came from. Left out entirely when the API has no answer,
                    so a card can still be entered. */}
                {viewerCountry?.country && (
                    <input
                        type="hidden"
                        name="country"
                        data-recurly="country"
                        value={viewerCountry.country}
                        readOnly
                    />
                )}
                {showAvailableCards && (
                    <Form.Item
                        className="accepted-cards"
                        label="Accepted cards"
                    >
                        {availableCards.map((card) => (
                            <img
                                key={card.name}
                                src={card.imageSrc}
                                alt={card.name}
                                title={card.name}
                            />
                        ))}
                    </Form.Item>
                )}

                <Form.Item
                    className="card-number"
                    validateStatus={validationErrors.number ? 'error' : 'validating'}
                    help={validationErrors.number ? 'Invalid card number' : ''}
                >
                    <CardNumberElement
                        className="card-number-input"
                        onFocus={handleFocus}
                        onChange={setCardNumberValidation}
                        onSubmit={handleSubmit}
                        displayIcon={true}
                        style={{
                            // @ts-ignore
                            placeholder: { content: 'Card number', color: 'rgba(66,71,71,.64)', fontWeight: '300' },
                        }}
                    />
                </Form.Item>

                <Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="card-expiration-date">
                                <Form.Item
                                    validateStatus={validationErrors['expirationDate'] ? 'error' : 'validating'}
                                    help={validationErrors['expirationDate'] ? 'Invalid expiration date' : ''}
                                >
                                    <Input.Group compact>
                                        <Row>
                                            <Col span={12}>
                                                <CardMonthElement
                                                    className="card-month-input"
                                                    onFocus={handleFocus}
                                                    onSubmit={handleSubmit}
                                                    onChange={setCardMonthValidation}
                                                    inputType="select"
                                                    style={{
                                                        // @ts-ignore
                                                        placeholder: { content: 'MM', color: 'rgba(66,71,71,.64)', fontWeight: '300' },
                                                    }}
                                                />
                                            </Col>
                                            <Col span={12}>
                                                <CardYearElement
                                                    className="card-year-input"
                                                    onFocus={handleFocus}
                                                    onSubmit={handleSubmit}
                                                    onChange={setCardYearValidation}
                                                    inputType="select"
                                                    style={{
                                                        // @ts-ignore
                                                        placeholder: { content: 'YY', color: 'rgba(66,71,71,.64)', fontWeight: '300' },
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </Input.Group>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="card-verification-code">
                                <Form.Item
                                    validateStatus={validationErrors['cvc'] ? 'error' : 'validating'}
                                    help={validationErrors['cvc'] ? 'Invalid CVV' : ''}
                                >
                                    <CardCvvElement
                                        className="card-cvv-input"
                                        onFocus={handleFocus}
                                        onSubmit={handleSubmit}
                                        onChange={setCvcValidation}
                                        style={{
                                            // @ts-ignore
                                            placeholder: { content: 'CVV', color: 'rgba(66,71,71,.64)', fontWeight: '300' },
                                        }}
                                    />
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            className="first-name-input"
                            validateStatus={validationErrors['firstName'] ? 'error' : 'validating'}
                            help={validationErrors['firstName'] ? 'Invalid name' : ''}
                        >
                            <Input
                                name="first_name"
                                data-recurly="first_name"
                                placeholder="First name"
                                spellCheck="false"
                                onChange={(event) => setFirstName(event.target.value)}
                                onFocus={handleNameFocus}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            className="last-name-input"
                            validateStatus={validationErrors['lastName'] ? 'error' : 'validating'}
                            help={validationErrors['lastName'] ? 'Invalid name' : ''}
                        >
                            <Input
                                name="last_name"
                                data-recurly="last_name"
                                placeholder="Last name"
                                spellCheck="false"
                                onChange={(event) => setLastName(event.target.value)}
                                onFocus={handleNameFocus}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row>
                    <Form.Item
                        className="tos-form-item"
                        validateStatus={validationErrors['acceptTos'] ? 'error' : 'validating'}
                        help={validationErrors['acceptTos'] ? 'You must agree with our terms of service!' : ''}
                    >
                        <Checkbox
                            className="accept-tos-checkbox"
                            onChange={handleAcceptTosChange}
                        >
                            I accept the&nbsp;
                            <a
                                href={SSC_TOS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Terms of Service
                            </a>
                        </Checkbox>
                    </Form.Item>

                    <Form.Item className="place-order-form-item">
                        <Button
                            className={`place-order ${!acceptTos ? 'disabled' : ''}`}
                            type="primary"
                            htmlType="submit"
                            block
                            loading={isLoading || isLoadingViewerCountry}
                        >
                            {buttonLabel}
                            <Icon
                                type="lock"
                                theme="filled"
                            />
                        </Button>
                    </Form.Item>
                </Row>
            </form>
        </>
    );
}
